import { footprintPolygonsToClipping } from '@alpinehomeair/common';
import { MIN_SPACE_DIM_FEET } from 'data/constants';
import { FootprintPolygonsType, LevelType, PositionType } from 'data/types';
import polygonClipping, { MultiPolygon } from 'polygon-clipping';
import { collectLevelUserPlacedSpaces } from 'utils/auto-layout-space';
import {
  clippingOuters,
  geometryFromAbsolutePolygon,
  polygonToClipping,
  spaceAbsolutePolygon
} from 'utils/carve-auto-layout-space';
import { DrawnSpaceShapeResult } from 'utils/drawn-space-shape-types';
import {
  findPolygonForPoints,
  hasAnyFootprintPolygon
} from 'utils/footprint-polygons';
import { freehandToFootprintShape } from 'utils/freehand-outline';
import { snapPolygonVerticesToGrid } from 'utils/polygon-grid-snap';
import { polygonAreaAbs } from 'utils/space-gap-fill';
import {
  isPolygonInsideAnyFootprint,
  isPolygonSimple,
  polygonOverlapsAny
} from 'utils/space-polygon';

export const MIN_DRAWN_SPACE_AREA_SQFT =
  MIN_SPACE_DIM_FEET * MIN_SPACE_DIM_FEET;

function largestPolygon(polygons: PositionType[][]): PositionType[] | null {
  if (polygons.length === 0) return null;
  return polygons.reduce((largest, polygon) =>
    polygonAreaAbs(polygon) > polygonAreaAbs(largest) ? polygon : largest
  );
}

function clipToFootprints(
  shape: PositionType[],
  footprints: FootprintPolygonsType
): MultiPolygon | null {
  const shapeClip = polygonToClipping(shape);
  if (!hasAnyFootprintPolygon(footprints)) {
    return polygonClipping.union(shapeClip);
  }
  const clipped = polygonClipping.intersection(
    shapeClip,
    footprintPolygonsToClipping(footprints)
  );
  return clipped.length > 0 ? clipped : null;
}

function carveOtherSpaces(
  area: MultiPolygon,
  otherSpacePolygons: PositionType[][]
): PositionType[][] {
  const obstacles = otherSpacePolygons
    .filter((polygon) => polygon.length >= 3)
    .map(polygonToClipping);
  const remaining =
    obstacles.length > 0
      ? polygonClipping.difference(area, ...obstacles)
      : area;
  return clippingOuters(remaining).polygons;
}

function isPlaceable(
  vertices: PositionType[],
  footprints: FootprintPolygonsType,
  otherSpacePolygons: PositionType[][]
): boolean {
  if (vertices.length < 3 || !isPolygonSimple(vertices)) return false;
  if (geometryFromAbsolutePolygon(vertices) === null) return false;
  if (polygonAreaAbs(vertices) < MIN_DRAWN_SPACE_AREA_SQFT) return false;
  if (!isPolygonInsideAnyFootprint(vertices, footprints)) {
    return false;
  }
  return !polygonOverlapsAny(vertices, otherSpacePolygons);
}

export function collectUserPlacedSpacePolygons(
  level: LevelType,
  autoLayoutSpaceIds: readonly string[]
): PositionType[][] {
  return collectLevelUserPlacedSpaces(level, autoLayoutSpaceIds)
    .map(spaceAbsolutePolygon)
    .filter((polygon): polygon is PositionType[] => polygon !== null);
}

export function resolveDrawnSpaceShape(
  rawStroke: PositionType[],
  footprints: FootprintPolygonsType,
  otherSpacePolygons: PositionType[][]
): DrawnSpaceShapeResult {
  const shape = freehandToFootprintShape(rawStroke, MIN_DRAWN_SPACE_AREA_SQFT);
  if (!shape) return { status: 'invalid-stroke' };

  const clipped = clipToFootprints(shape, footprints);
  if (!clipped) return { status: 'outside-footprint' };

  const largest = largestPolygon(carveOtherSpaces(clipped, otherSpacePolygons));
  if (!largest) return { status: 'no-room' };

  const vertices =
    snapPolygonVerticesToGrid(
      largest,
      findPolygonForPoints(largest, footprints),
      otherSpacePolygons
    ) ?? largest;
  if (!isPlaceable(vertices, footprints, otherSpacePolygons)) {
    return { status: 'no-room' };
  }

  return {
    status: 'resolved',
    vertices,
    areaSqft: Math.ceil(polygonAreaAbs(vertices))
  };
}
